import { useRef, useState, type ReactNode } from "react";
import {
  applyMore,
  barPct,
  BREAKDOWN_PAGE,
  dimLabel,
  fmtInt,
  morePages,
  rowLabel,
  type Breakdown,
  type BreakdownRow,
  type Filter,
  type MorePages,
} from "../../web-analytics";

export type PanelDim = { dim: string; label: string };

type Props = {
  title: string;
  caption?: string;
  /** One tab per dim; a single dim shows no tabs. */
  dims: PanelDim[];
  /** stats.breakdowns: the first page of every dim. */
  rows: Record<string, BreakdownRow[]>;
  filters: Filter[];
  onPick: (dim: string, value: string) => void;
  /** GET /breakdown/:dim from `offset`, for "Show more", with the page's current query. */
  loadMore: (dim: string, offset: number) => Promise<Breakdown>;
  /** The query `rows` answer. "Show more" pages are kept per key and never cross to another. */
  queryKey: string;
  /** New stats are loading; the rows on screen are the previous query's. */
  busy: boolean;
  /** Drawn with the list (the Countries map): above it on a phone, beside it when wide. */
  visual?: ReactNode;
  className?: string;
};

/**
 * One breakdown: a label, a share bar and the visitor count per row. A row
 * click filters the whole page by it; clicking the row already picked removes
 * that filter. Ported from Sonar's BreakdownPanel; tabs and "Show more" are
 * local state, so switching a tab is not a history entry.
 */
export default function BreakdownPanel({ title, caption, dims, rows, filters, onPick, loadMore, queryKey, busy, visual, className }: Props) {
  // Open on the tab whose dim is filtered, so a picked browser stays in view.
  const [tab, setTab] = useState(() => (dims.find((d) => filters.some((f) => f.dim === d.dim)) ?? dims[0]).dim);
  // Extra pages belong to one query; a new range or filter starts over.
  const [more, setMore] = useState<MorePages>({ key: queryKey, byDim: {} });
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Read when a response lands, so it is judged against the query on screen THEN.
  const current = useRef({ key: queryKey, busy });
  current.current = { key: queryKey, busy };

  // While busy the rows are stale, so their extra pages (and the button) are hidden too.
  const extra = busy ? undefined : morePages(more, queryKey, tab);
  const list = extra?.rows ?? rows[tab] ?? [];
  const hasMore = extra ? extra.hasMore : list.length >= BREAKDOWN_PAGE;
  const max = Math.max(0, ...list.map((r) => r.visitors));
  const active = filters.find((f) => f.dim === tab)?.value;
  const label = dimLabel(tab);

  const showMore = () => {
    if (busy) return;
    const req = { key: queryKey, dim: tab, shown: list };
    const stale = () => current.current.busy || current.current.key !== req.key;
    setPending(req.dim);
    setError(null);
    loadMore(req.dim, list.length)
      .then((res) => setMore((m) => applyMore(m, req, res, current.current)))
      .catch((e: unknown) => { if (!stale()) setError(e instanceof Error ? e.message : "Could not load more rows"); })
      .finally(() => setPending(null));
  };

  return (
    <section className={`panel wa-bd${className ? ` ${className}` : ""}`}>
      <div className="panel-heading">
        <div className="wa-bd-title">
          <h2>{title}</h2>
          {caption && <p className="panel-meta">{caption}</p>}
        </div>
        {dims.length > 1 && (
          <div className="segmented-control compact wa-bd-tabs" role="group" aria-label={`${title} breakdown`}>
            {dims.map((d) => (
              <button key={d.dim} type="button" aria-pressed={tab === d.dim} className={tab === d.dim ? "active" : ""}
                onClick={() => { setTab(d.dim); setError(null); }}>
                {d.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Its own full-width row, never a tooltip: the note is the reason the
          rows can add up to more than the headline, so it must be readable. */}
      {active !== undefined && (
        <p className="wa-bd-note">This panel ignores its own filter, so numbers can exceed the total.</p>
      )}

      <div className="wa-bd-main">
        {visual}

        {list.length === 0 ? (
          <div className="wa-bd-empty">No data in this range</div>
        ) : (
          <div className="wa-bd-body">
            <div className="wa-bd-head" aria-hidden="true"><span>{label}</span><span>Visitors</span></div>
            <ul className="wa-bd-list">
              {list.map((r) => {
                const on = active === r.value;
                const text = rowLabel(tab, r.value);
                return (
                  <li key={r.value}>
                    <button
                      type="button"
                      className={on ? "wa-bd-row active" : "wa-bd-row"}
                      aria-pressed={on}
                      aria-label={on ? `Remove filter: ${label} ${text}` : `Filter by ${label} ${text}`}
                      title={on ? `Remove filter: ${r.value}` : r.value}
                      onClick={() => onPick(tab, r.value)}
                    >
                      <span className="wa-bd-bar" style={{ width: `${barPct(r.visitors, max)}%` }} aria-hidden="true" />
                      <span className="wa-bd-label">{text}</span>
                      <span className="wa-bd-count">{fmtInt(r.visitors)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {error && <p className="wa-bd-error" role="alert">{error}</p>}
            {hasMore && (
              <button type="button" className="wa-bd-more" disabled={busy || pending === tab} onClick={showMore}>
                {pending === tab ? "Loading…" : "Show more"}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
